import { Asset, MetadataFieldType } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { AssetMatch, BulkUploadData } from '../types';
import {
  RenderAssetMatchingAction,
  RenderAssetMatchingStatus,
  RenderAssetNameWithThumbnail,
  RenderConfirmationMetadataUpdates,
  RenderDetails,
  RenderErrorMessage,
  RenderMetadataUpdates,
  RenderStatusChip,
} from './cellDefinitions';

const getResultsBaseColumns = (getAssetUrl: (assetId: string) => string, data: BulkUploadData): GridColDef[] => [
  {
    field: 'assetName',
    headerName: 'Asset',
    width: 300,
    sortable: false,
    filterable: false,
    flex: 1,
    renderCell: parameters => RenderAssetNameWithThumbnail(parameters, getAssetUrl, data),
  },
];

const getSuccessfulColumns = (getAssetUrl: (assetId: string) => string, data: BulkUploadData): GridColDef[] => [
  ...getResultsBaseColumns(getAssetUrl, data),
  {
    field: 'updatedFields',
    headerName: 'Updated Fields',
    width: 300,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderMetadataUpdates(parameters.row.updatedFields),
  },
];

const getFailedColumns = (getAssetUrl: (assetId: string) => string, data: BulkUploadData): GridColDef[] => [
  ...getResultsBaseColumns(getAssetUrl, data),
  {
    field: 'errorMessage',
    headerName: 'Error Message',
    width: 250,
    renderCell: parameters => RenderErrorMessage(parameters.row.errorMessage),
  },
  {
    field: 'updatedFields',
    headerName: 'Attempted Updates',
    width: 200,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderMetadataUpdates(parameters.row.updatedFields),
  },
];

const getAllResultsColumns = (getAssetUrl: (assetId: string) => string, data: BulkUploadData): GridColDef[] => [
  {
    field: 'assetName',
    headerName: 'Asset',
    width: 300,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderAssetNameWithThumbnail(parameters, getAssetUrl, data),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: parameters => RenderStatusChip(parameters.row.status),
  },
  {
    field: 'details',
    headerName: 'Details',
    width: 300,
    sortable: false,
    filterable: false,
    renderCell: RenderDetails,
  },
];

export const createResultsStepColumns = (
  getAssetUrl: (assetId: string) => string,
  data: BulkUploadData
): {
  successfulColumns: GridColDef[];
  failedColumns: GridColDef[];
  allResultsColumns: GridColDef[];
} => ({
  successfulColumns: getSuccessfulColumns(getAssetUrl, data),
  failedColumns: getFailedColumns(getAssetUrl, data),
  allResultsColumns: getAllResultsColumns(getAssetUrl, data),
});

export const createConfirmationStepColumns = (
  getAssetUrl: (assetId: string) => string,
  data: BulkUploadData
): GridColDef[] => [
  {
    field: 'name',
    headerName: 'Name',
    width: 300,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderAssetNameWithThumbnail(parameters, getAssetUrl, data),
  },
  {
    field: 'metadataUpdates',
    headerName: 'Metadata Updates',
    width: 400,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderConfirmationMetadataUpdates(parameters, data),
  },
];

export const createAssetMatchingStepColumns = (
  getStatusIcon: (status: AssetMatch['status']) => React.ReactNode,
  getStatusText: (match: AssetMatch) => string,
  openAssetSelectionDialog: (assetName: string, matches: Asset[], currentSelection?: Asset) => void,
  getAssetUrl: (assetId: string) => string,
  data: BulkUploadData
): GridColDef[] => [
  {
    field: 'name',
    headerName: 'Name',
    width: 300,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderAssetNameWithThumbnail(parameters, getAssetUrl, data),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 300,
    renderCell: parameters => RenderAssetMatchingStatus(parameters, getStatusIcon, getStatusText),
  },
  {
    field: 'action',
    headerName: 'Action',
    width: 150,
    sortable: false,
    filterable: false,
    renderCell: parameters => RenderAssetMatchingAction(parameters, openAssetSelectionDialog),
  },
];

export const createDataPreviewStepColumns = (metadataFields: string[]): GridColDef[] => [
  {
    field: 'assetName',
    headerName: 'Path',
    width: 200,
    flex: 1,
  },
  ...metadataFields.map(field => ({
    field: `metadata.${field}`,
    headerName: field,
    width: 150,
    renderCell: (parameters: { row: { metadata: Record<string, string> } }) => parameters.row.metadata[field] || '-',
  })),
];

const getFieldTypeDisplayName = (type: MetadataFieldType) => {
  switch (type) {
    case MetadataFieldType.Text: {
      return 'Text';
    }
    case MetadataFieldType.Number: {
      return 'Number';
    }
    case MetadataFieldType.Boolean: {
      return 'Boolean';
    }
    default: {
      return type;
    }
  }
};

export const getFieldTypesStepColumns = (
  data: BulkUploadData,
  handleFieldTypeChange: (fieldName: string, type: MetadataFieldType) => void
) => [
  {
    field: 'name',
    headerName: 'Field Name',
    width: 200,
    renderCell: (parameters: { row: { name: string } }) => (
      <BodyText fontWeight='medium'>{parameters.row.name}</BodyText>
    ),
  },
  {
    field: 'sampleValues',
    headerName: 'Sample Values',
    width: 250,
    renderCell: (parameters: { row: { name: string } }) => {
      const sampleValues = data.parsedData
        .map((row: { metadata: Record<string, string> }) => row.metadata[parameters.row.name])
        .filter(Boolean)
        .slice(0, 3);

      return (
        <BodyText color='textSecondary'>
          {sampleValues.join(', ')}
          {sampleValues.length === 0 && '-'}
        </BodyText>
      );
    },
  },
  {
    field: 'type',
    headerName: 'Data Type',
    width: 200,
    renderCell: (parameters: { row: { name: string; type: MetadataFieldType } }) => (
      <FormControl size='small' sx={{ minWidth: 120 }}>
        <InputLabel id={`field-type-${parameters.row.name}`}>Type</InputLabel>
        <Select
          labelId={`field-type-${parameters.row.name}`}
          value={parameters.row.type}
          label='Type'
          onChange={event => handleFieldTypeChange(parameters.row.name, event.target.value as MetadataFieldType)}
        >
          <MenuItem value={MetadataFieldType.Text}>{getFieldTypeDisplayName(MetadataFieldType.Text)}</MenuItem>
          <MenuItem value={MetadataFieldType.Number}>{getFieldTypeDisplayName(MetadataFieldType.Number)}</MenuItem>
          <MenuItem value={MetadataFieldType.Boolean}>{getFieldTypeDisplayName(MetadataFieldType.Boolean)}</MenuItem>
        </Select>
      </FormControl>
    ),
  },
];
